import { useEffect, useState } from 'react'

const RADIUS = 80
const FULL_ARC = Math.PI * RADIUS  // ≈ 251.33 — length of a semicircle

function gaugeColor(hf: number): string {
  if (hf >= 2.0) return '#22c55e'
  if (hf >= 1.5) return '#eab308'
  return '#ef4444'
}

function gaugeLabel(hf: number): string {
  if (hf >= 2.0) return 'Safe'
  if (hf >= 1.5) return 'Caution'
  return 'Danger'
}

interface Props {
  healthFactor: number
}

export function HealthGauge({ healthFactor }: Props) {
  const hf = Math.max(0, healthFactor)
  const [animated, setAnimated] = useState(false)

  // Kick off the fill animation on first mount
  useEffect(() => {
    const t = window.setTimeout(() => setAnimated(true), 80)
    return () => window.clearTimeout(t)
  }, [])

  const ratio = Math.min(1, hf / 4)  // 0–4 HF maps to 0–100% of arc
  const dashOffset = animated ? FULL_ARC * (1 - ratio) : FULL_ARC
  const color = gaugeColor(hf)
  const hfDisplay = hf >= 999990 ? '∞' : hf.toFixed(2)

  // Zone tick marks at 1.5 and 2.0 HF
  const tickAngle = (v: number) => {
    const r = Math.min(1, v / 4)
    const angle = Math.PI * (1 - r) // 0 = right, π = left
    return {
      x1: 100 + (RADIUS - 16) * Math.cos(angle),
      y1: 100 - (RADIUS - 16) * Math.sin(angle),
      x2: 100 + (RADIUS + 2) * Math.cos(angle),
      y2: 100 - (RADIUS + 2) * Math.sin(angle),
    }
  }
  const t1 = tickAngle(1.5)
  const t2 = tickAngle(2.0)

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 115" className="w-52 h-28">
        {/* Background track */}
        <path
          d="M 20 100 A 80 80 0 0 0 180 100"
          fill="none"
          stroke="#ffffff0f"
          strokeWidth="14"
          strokeLinecap="round"
        />
        {/* Animated fill arc (CCW = top arc) */}
        <path
          d="M 20 100 A 80 80 0 0 0 180 100"
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${FULL_ARC} ${FULL_ARC}`}
          strokeDashoffset={dashOffset}
          style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1), stroke 0.4s ease' }}
        />
        {/* Zone tick marks */}
        <line {...t1} stroke="#eab308" strokeWidth="1.5" opacity="0.6" />
        <line {...t2} stroke="#22c55e" strokeWidth="1.5" opacity="0.6" />
        {/* Zone labels */}
        <text x="24" y="112" fill="#ef4444" fontSize="8" opacity="0.7" textAnchor="middle">1.0</text>
        <text x="100" y="22" fill="#22c55e" fontSize="8" opacity="0.7" textAnchor="middle">4.0</text>
        <text x="176" y="112" fill="#22c55e" fontSize="8" opacity="0.7" textAnchor="middle">Safe</text>
        {/* Main HF value */}
        <text x="100" y="82" textAnchor="middle" fill="white" fontSize="26" fontWeight="700">
          {hfDisplay}
        </text>
        <text x="100" y="96" textAnchor="middle" fill={color} fontSize="10" fontWeight="600">
          {gaugeLabel(hf)}
        </text>
        <text x="100" y="108" textAnchor="middle" fill="#6b7280" fontSize="9">
          Health Factor
        </text>
      </svg>
    </div>
  )
}
